/**
 * Settings · Account · API Keys (task 974).
 *
 * The account's API keys, in the one place they can be reviewed and taken away
 * again. Until this page existed the app could only MINT a key — the Developer
 * Info tab's shortcut — so a leaked or rotated key could not be revoked without
 * calling the service by hand, even though `GET /api-keys` and
 * `DELETE /api-keys/:id` have been there since the keys themselves.
 *
 * Two things about the data are worth knowing when reading this file:
 *
 *  1. **A listed key is an active key.** Revocation is a soft delete and the
 *     list route returns only rows with no `deletedAt`, so there is no status
 *     column and no `active` field on `ApiKey` — a revoked key simply leaves
 *     the table on the next read.
 *  2. **The plaintext exists for exactly one render.** The mint response is the
 *     only time the secret is ever available; nothing here can fetch it back,
 *     which is why the reveal panel says so and stays on screen until the User
 *     dismisses it rather than closing on a click elsewhere.
 *
 * Mint and revoke are gated backend-side by `innovate.be.api-keys.manage`,
 * which the browser cannot read. So the page does not guess: it calls, and if
 * the service answers 403 "Feature disabled" it says that in a note and leaves
 * the table read-only for the rest of the visit.
 *
 * The UI has its own lever too, `innovate.fe.settings-api-keys.manage` (task
 * 1218): off, the Create card and every Revoke are gone and the same note says
 * the keys are read-only. The page itself stays on `settings-api-keys.page`.
 *
 * A key is also external access, which is a PAID capability (task 1020): minting
 * on a Free plan is refused with 403 `feature_not_entitled`. That refusal is an
 * entitlement matter, not a page-local failure — the api layer hands it to
 * `EntitlementGuard`, and the page renders the same `UpgradePrompt` where the
 * mint form sits rather than restating the reason in a toast of its own.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '../components/ui/DataTable';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { useFlag } from '../hooks/useFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { apiKeysApi, type ApiKey, type MintedApiKey } from '../services/api';
import UpgradePrompt from '../components/ui/UpgradePrompt';
import { extractEntitlementError, isFeatureNotEntitledError } from '../types/billing';
import type { FeatureNotEntitledError } from '../types/billing';
import { asApiRefusal, refusalReason } from '../utils/apiRefusal';

export const API_KEYS_QUERY_KEY = ['account', 'api-keys'] as const;

/** "Sep 17, 2026" — the day a key was minted. */
const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * True when the service refused because `innovate.be.api-keys.manage` is off.
 * The feature gate answers 403 with its own message, which is the only signal
 * the browser gets — there is no frontend read of a backend flag.
 */
const isFeatureDisabled = (error: unknown): boolean => {
  const refusal = asApiRefusal(error);
  if (refusal.status !== 403) return false;
  return /feature (disabled|check unavailable)/i.test(refusal.message ?? '');
};

export default function ApiKeysPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState('');
  const [labelError, setLabelError] = useState<string | undefined>(undefined);
  const [mintedKey, setMintedKey] = useState<MintedApiKey | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);
  /** Set once the service has said management is off; never unset in a visit. */
  const [manageDisabled, setManageDisabled] = useState(false);
  /** Mint and revoke's own UI lever (task 1218). */
  const manageEnabled = useFlag('innovate.fe.settings-api-keys.manage');
  /** The entitlement refusal behind a failed mint, while the prompt is shown. */
  const [entitlementRefusal, setEntitlementRefusal] = useState<FeatureNotEntitledError | null>(
    null,
  );

  const {
    data: keys = [],
    isLoading,
    isError,
  } = useQuery<ApiKey[]>({
    queryKey: API_KEYS_QUERY_KEY,
    queryFn: () => apiKeysApi.list({ limit: 100 }).then((res) => res.data.data),
    staleTime: 30_000,
    retry: 1,
  });

  const mint = useMutation({
    mutationFn: (keyLabel: string) => apiKeysApi.mint(keyLabel).then((res) => res.data.data),
    onSuccess: (key) => {
      setMintedKey(key);
      setLabel('');
      setEntitlementRefusal(null);
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
      toast.success('API key created', 'Copy it now — it is shown only once.');
    },
    onError: (error) => {
      // 403 feature_not_entitled: the plan does not include external access
      // (task 1020). The prompt below says what the plan is missing and how to
      // fix it, so no toast competes with it.
      const entitlement = extractEntitlementError(error);
      if (entitlement && isFeatureNotEntitledError(entitlement)) {
        setEntitlementRefusal(entitlement);
        return;
      }
      if (isFeatureDisabled(error)) {
        setManageDisabled(true);
        toast.error('Creating API keys is turned off', refusalReason(error));
        return;
      }
      toast.error('Failed to create API key', refusalReason(error));
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
      toast.success('API key revoked', 'It can no longer authenticate anywhere.');
    },
    onError: (error) => {
      if (isFeatureDisabled(error)) {
        setManageDisabled(true);
        toast.error('Revoking API keys is turned off', refusalReason(error));
        return;
      }
      toast.error('Failed to revoke API key', refusalReason(error));
    },
  });

  const handleMint = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) {
      setLabelError('Give the key a label so it can be told apart later.');
      return;
    }
    setLabelError(undefined);
    mint.mutate(trimmed);
  };

  const handleCopyKey = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('API key copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revoke.mutateAsync(pendingRevoke.id);
    } finally {
      setPendingRevoke(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="API Keys"
        description={
          <>
            Keys let a caller outside the browser — a server, a script, a copied-out interface —
            reach this account&apos;s AI Services with the{' '}
            <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              x-api-key
            </code>{' '}
            header. Any active key opens every API-key AI Service in the account.
          </>
        }
      />

      {(manageDisabled || !manageEnabled) && (
        <div
          data-testid="api-keys-manage-disabled"
          className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
        >
          Creating and revoking keys is turned off for this environment. The keys below are shown
          read-only and keep working until management is switched back on.
        </div>
      )}

      {/* Mint — gone with the UI lever off (task 1218). */}
      {manageEnabled && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Create an API key</h2>
          <form className="mt-3 flex flex-wrap items-start gap-3" onSubmit={handleMint}>
            <div className="min-w-0 flex-1">
              <label htmlFor="api-key-label" className="sr-only">
                Label
              </label>
              <input
                id="api-key-label"
                name="api-key-label"
                type="text"
                value={label}
                disabled={manageDisabled}
                onChange={(event) => {
                  setLabel(event.target.value);
                  if (labelError) setLabelError(undefined);
                }}
                placeholder="Label — what will use this key?"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
              {labelError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="api-key-label-error">
                  {labelError}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={mint.isPending || manageDisabled}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              <KeyIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              {mint.isPending ? 'Creating…' : 'Create API key'}
            </button>
          </form>

          {/* External access needs a paid plan (task 1021). The shared prompt, at
              the form that was refused — the same one EntitlementGuard renders. */}
          {entitlementRefusal && (
            <div className="mt-3" data-testid="api-keys-upgrade-prompt">
              <UpgradePrompt
                error={entitlementRefusal}
                onDismiss={() => setEntitlementRefusal(null)}
              />
            </div>
          )}

          {/* One-time reveal. Nothing can fetch this value back. */}
          {mintedKey && (
            <div
              data-testid="minted-key-reveal"
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/30"
            >
              <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                Your new API key — copy it now, it will not be shown again.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 truncate rounded border border-amber-200 bg-white px-2 py-1.5 font-mono text-xs text-gray-900 dark:border-amber-800 dark:bg-gray-900 dark:text-gray-100">
                  {mintedKey.key}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopyKey(mintedKey.key)}
                  className="inline-flex shrink-0 items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  Copy key
                </button>
                <button
                  type="button"
                  onClick={() => setMintedKey(null)}
                  className="inline-flex shrink-0 items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* List */}
      <div className="mt-6">
        {isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="api-keys-loading">
            Loading API keys…
          </p>
        ) : isError ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="api-keys-error">
            The account&apos;s API keys could not be loaded. Try again in a moment.
          </p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="api-keys-empty">
            No API keys yet. Create one above to call this account&apos;s AI Services from outside
            the browser.
          </p>
        ) : (
          <DataTable>
            <DataTableHead>
              <TableHeader>Label</TableHeader>
              <TableHeader>Key</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Last used</TableHeader>
              <TableHeader className="text-right">Actions</TableHeader>
            </DataTableHead>
            <DataTableBody>
              {keys.map((key) => (
                <DataTableRow key={key.id}>
                  <DataTableCell className="text-sm text-gray-900 dark:text-white">
                    <span data-testid={`api-key-row-${key.id}`}>{key.label || 'Unlabeled key'}</span>
                  </DataTableCell>
                  <DataTableCell className="font-mono text-sm text-gray-500 dark:text-gray-400">
                    {key.prefix}…
                  </DataTableCell>
                  <DataTableCell className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(key.createdAt)}
                  </DataTableCell>
                  <DataTableCell className="text-sm text-gray-500 dark:text-gray-400">
                    {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never used'}
                  </DataTableCell>
                  <DataTableCell className="text-right">
                    {!manageDisabled && manageEnabled && (
                      <button
                        type="button"
                        onClick={() => setPendingRevoke(key)}
                        className="text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Revoke
                      </button>
                    )}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </div>

      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        An AI Service decides whether a key is needed at all — see its Developer Info tab for the
        call it expects.{' '}
        <Link
          to="/endpoints"
          className="font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          Go to AI Services
        </Link>
      </p>

      <ConfirmDialog
        isOpen={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        onConfirm={confirmRevoke}
        title="Revoke this API key?"
        message={
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {pendingRevoke?.label || 'This key'} ({pendingRevoke?.prefix}…) stops authenticating
            immediately, everywhere it is in use. This cannot be undone — a replacement is a new
            key.
          </p>
        }
        confirmLabel="Revoke key"
        confirmStyle="danger"
        isLoading={revoke.isPending}
      />
    </div>
  );
}
